import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { TDR, querySnapshotToArray } from 'tdr-common';

const CACHE_SETTINGS = {
	staleTime: 5 * 60 * 1000, // 5 minutes - tables rarely change
	gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
	refetchOnMount: false,
	refetchOnWindowFocus: false
};

export function useTables(restaurantId: string, includeDisabledTables = false) {

	const query = useQuery({
		enabled: !!restaurantId,
		queryKey: ['restaurantTables', restaurantId, includeDisabledTables],
		queryFn: () => getRestaurantTables(restaurantId, includeDisabledTables),
		...CACHE_SETTINGS
	});

	return query;
}

/**
 * Hook for fetching restaurant tables with React Suspense
 * Use this in components wrapped with <Suspense> for automatic loading states
 * @throws {Promise} Suspends while loading
 */
export function useTablesSuspense(restaurantId: string, includeDisabledTables = false) {
	const query = useSuspenseQuery({
		queryKey: ['restaurantTables', restaurantId, includeDisabledTables],
		queryFn: () => getRestaurantTables(restaurantId, includeDisabledTables),
		...CACHE_SETTINGS
	});

	return query;
}

export async function getRestaurantTables(
	restaurantId: string,
	includeDisabledTables = false
): Promise<TDR.Table[]> {
	try {
		const db = getFirestore();
		const q = query(
			collection(db, 'Tables'),
			where('restaurantId', '==', restaurantId)
		);

		const querySnapshot = await getDocs(q);
		let allTables: TDR.Table[] = querySnapshotToArray(querySnapshot) || [];

		allTables = allTables.filter(t => t.deleted !== true || t.deleted === undefined);

		if (includeDisabledTables === false) {
			return allTables.filter(t => !(t.disabled === true));
		}
		else {
			return allTables;
		}
	}
	catch (error) {
		console.error('Error getting restaurant tables', error);
		return [];
	}
}
